import { Check, Circle, X } from 'lucide-react'
import type { AggregatorOrgProfile } from '../../types/api'

type State = 'done' | 'current' | 'waiting' | 'failed'

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : null)

const DPG_DONE = ['registered', 'registered_as_aggregator']

/**
 * An organisation's journey on the partner portal, start to finish: registered, approved by a
 * super admin (V56), connected to the DPG network (V55), coordinators invited, coordinators joined,
 * people onboarded. Each stage says where it stands, so nobody has to ask what happens next.
 */
export function OrgProgress({ me }: { me: AggregatorOrgProfile }) {
  const approved = me.approvalStatus === 'APPROVED' || me.approvalStatus === 'SUSPENDED'
  const dpgDone = DPG_DONE.includes(me.dpgStatus ?? '')
  const dpgFailed = me.dpgStatus === 'failed' || me.dpgStatus === 'exists'
  const people = me.studentsOnboarded + me.tutorsOnboarded

  const stages: { label: string; state: State; detail: string }[] = [
    { label: 'Registered', state: 'done', detail: fmt(me.createdAt) ?? '' },
    {
      label: 'Approved by YellowDots',
      state: me.approvalStatus === 'REJECTED' ? 'failed' : approved ? 'done' : 'current',
      detail:
        me.approvalStatus === 'REJECTED'
          ? `Not approved${me.decisionReason ? `: ${me.decisionReason}` : ''}`
          : approved
            ? `Approved ${fmt(me.decidedAt) ?? ''}`.trim()
            : 'Waiting for review',
    },
    {
      label: 'Connected to the DPG network',
      state: !approved ? 'waiting' : dpgDone || me.invitesOpen ? 'done' : dpgFailed ? 'failed' : 'current',
      detail: !approved
        ? 'After approval'
        : dpgDone
          ? 'Connected'
          : me.invitesOpen
            ? 'Not needed here'
            : dpgFailed
              ? (me.dpgSyncError ?? 'Did not go through - being retried')
              : 'Connecting...',
    },
    {
      label: 'Coordinators invited',
      state: me.invitesSent > 0 ? 'done' : me.invitesOpen ? 'current' : 'waiting',
      detail: me.invitesSent > 0 ? `${me.invitesSent} sent` : me.invitesOpen ? 'Invite your first coordinator' : 'Once connected',
    },
    {
      label: 'Coordinators joined',
      state: me.invitesAccepted > 0 ? 'done' : me.invitesSent > 0 ? 'current' : 'waiting',
      detail: me.invitesSent > 0 ? `${me.invitesAccepted} of ${me.invitesSent}` : '-',
    },
    {
      label: 'People onboarded',
      state: people > 0 ? 'done' : me.invitesAccepted > 0 ? 'current' : 'waiting',
      detail: people > 0 ? `${me.studentsOnboarded} learners · ${me.tutorsOnboarded} tutors` : '-',
    },
  ]

  return (
    <section className="card-pad">
      <h2 className="font-bold text-ink-900">Your progress</h2>
      <ol className="mt-3 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {stages.map((s) => (
          <li key={s.label} className="flex items-start gap-2">
            <span
              className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full ${
                s.state === 'done'
                  ? 'bg-green-600 text-white'
                  : s.state === 'failed'
                    ? 'bg-red-600 text-white'
                    : s.state === 'current'
                      ? 'bg-sun-400 text-ink-900'
                      : 'bg-ink-100 text-ink-400'
              }`}
              aria-hidden="true"
            >
              {s.state === 'done' ? <Check size={14} /> : s.state === 'failed' ? <X size={14} /> : <Circle size={8} fill="currentColor" />}
            </span>
            <span className="min-w-0">
              <span className={`block text-sm font-semibold ${s.state === 'waiting' ? 'text-ink-400' : 'text-ink-900'}`}>{s.label}</span>
              <span className="block break-words text-xs text-ink-500">{s.detail}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}
